import { cn } from '@parcel/ui';
import * as React from 'react';

type CollapsedLineCount = 2 | 3 | 4 | 5 | 6;

interface ExpandableTextOptions {
  collapsedLines?: CollapsedLineCount;
  minCharacters?: number;
}

interface ExpandableTextProps extends ExpandableTextOptions {
  text: string;
  className?: string;
  toggleClassName?: string;
}

const LINE_CLAMP_CLASSES: Record<CollapsedLineCount, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

const APPROX_CHARS_PER_LINE = 88;

export function isExpandableText(
  text: string,
  { collapsedLines = 5, minCharacters }: ExpandableTextOptions = {},
) {
  const trimmed = text.trim();
  if (!trimmed) return false;

  const explicitLineCount = trimmed.split(/\r\n|\r|\n/).length;
  const characterThreshold = minCharacters ?? collapsedLines * APPROX_CHARS_PER_LINE;
  return explicitLineCount > collapsedLines || trimmed.length > characterThreshold;
}

export function ExpandableText({
  text,
  collapsedLines = 5,
  minCharacters,
  className,
  toggleClassName,
}: ExpandableTextProps) {
  const textId = React.useId();
  const textRef = React.useRef<HTMLParagraphElement>(null);
  const getHeuristicCanToggle = React.useCallback(
    () => isExpandableText(text, { collapsedLines, minCharacters }),
    [text, collapsedLines, minCharacters],
  );
  const [expanded, setExpanded] = React.useState(false);
  const [canToggle, setCanToggle] = React.useState(() => getHeuristicCanToggle());

  React.useEffect(() => {
    setExpanded(false);
    setCanToggle(getHeuristicCanToggle());
  }, [getHeuristicCanToggle]);

  React.useEffect(() => {
    if (expanded) return;

    const node = textRef.current;
    if (!node) return;

    const measure = () => {
      const overflows = node.scrollHeight > node.clientHeight + 1;
      setCanToggle(getHeuristicCanToggle() || overflows);
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
  }, [expanded, getHeuristicCanToggle]);

  return (
    <div>
      <p
        id={textId}
        ref={textRef}
        className={cn(
          'whitespace-pre-line',
          !expanded && canToggle && LINE_CLAMP_CLASSES[collapsedLines],
          className,
        )}
      >
        {text}
      </p>
      {canToggle ? (
        <button
          type="button"
          className={cn(
            'ml-auto mt-2 block text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            toggleClassName,
          )}
          aria-controls={textId}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      ) : null}
    </div>
  );
}
